// The approved click card (engine figures.click_card, Final/Portal/Pulsed/Click card) drawn in the browser for any
// click: rotated spectrum | smoothed Wigner-Ville (grey, cross-terms white) with upper / lower edge, ridge, maximum
// amplitude, start point and the Sweep / Length / Width arrows | measurement box | waveform + envelope.
// Same figure size (10 x 6.6 in at 120 dpi), grid positions, colours, line widths and texts as the engine.
import React, { useEffect, useMemo, useRef } from 'react';
import { cardData } from './engineCard';
import { LABEL } from './pulsedModel';
import { amax, amin } from './safeMath';

const FW = 1200; const FH = 792;                            // 10 x 6.6 in at 120 dpi
const PT = 120 / 72;                                        // points -> px
// gridspec(2, 2, width_ratios=[1, 3.3], height_ratios=[3, 1.15], wspace=.06, hspace=.1, l .08 r .98 t .94 b .08)
function grid() {
  const cw = 0.90 / (2 + 0.06); const sw = 0.06 * cw; const nw = (cw * 2) / 4.3;
  const ch = 0.86 / (2 + 0.1); const sh = 0.1 * ch; const nh = (ch * 2) / 4.15;
  const w0 = nw * 1; const w1 = nw * 3.3; const h0 = nh * 3; const h1 = nh * 1.15;
  const x0 = 0.08; const x1 = x0 + w0 + sw; const yTop0 = 0.94; const yTop1 = yTop0 - h0 - sh;
  const r = (x, w, yt, h) => ({ x: x * FW, y: (1 - yt) * FH, w: w * FW, h: h * FH });
  return { s: r(x0, w0, yTop0, h0), w: r(x1, w1, yTop0, h0), t: r(x1, w1, yTop1, h1) };
}
const G = grid();

// matplotlib MaxNLocator(nbins='auto', steps=[1, 2, 2.5, 5, 10]); x axes: 3 x font size per tick, y axes: 2 x
function ticks(lo, hi, axisPx, isX) {
  const a = Math.min(lo, hi); const b = Math.max(lo, hi);
  const nbins = Math.min(9, Math.max(1, Math.floor((axisPx / PT) / (10 * (isX ? 3 : 2)))));   // MaxNLocator 'auto': clipped to 9
  const raw = (b - a) / nbins; const scale = 10 ** Math.floor(Math.log10(raw));
  const steps = [1, 2, 2.5, 5, 10].map((v) => v * scale);
  for (const st of steps.filter((v) => v >= raw * 0.999)) {
    const start = Math.floor(a / st + 1e-9) * st; const out = [];
    for (let v = start; v <= b + st * 1e-9; v += st) if (v >= a - st * 1e-9) out.push(Math.round(v / st) * st);
    if (out.length >= 2) return out;
  }
  return [a, b];
}
const fmtTick = (v, step) => { const d = Math.max(0, -Math.floor(Math.log10(step + 1e-12)) + (String(step).includes('2.5') ? 1 : 0));
  const s = Math.abs(v) < step * 1e-6 ? (0).toFixed(d) : v.toFixed(d); return s.replace('-', '−'); };
const nanFmt = (v, f) => (v === null || v === undefined || Number.isNaN(v) ? 'nan' : f(v));

function Axis({ r, xlim, ylim, xlabel, ylabel, labelX = true, labelY = true, children, id }) {
  const X = (v) => r.x + ((v - xlim[0]) / (xlim[1] - xlim[0])) * r.w;
  const Y = (v) => r.y + r.h - ((v - ylim[0]) / (ylim[1] - ylim[0])) * r.h;
  const xt = ticks(xlim[0], xlim[1], r.w, true); const yt = ticks(ylim[0], ylim[1], r.h, false);
  const xs = xt.length > 1 ? Math.abs(xt[1] - xt[0]) : 1; const ys = yt.length > 1 ? Math.abs(yt[1] - yt[0]) : 1;
  const tl = 3.5 * PT; const fs = 10 * PT;
  return (
    <g>
      <clipPath id={id}><rect x={r.x} y={r.y} width={r.w} height={r.h} /></clipPath>
      <g clipPath={`url(#${id})`}>{children(X, Y)}</g>
      <path d={`M${r.x},${r.y} V${r.y + r.h} H${r.x + r.w}`} fill="none" stroke="#000" strokeWidth={0.8 * PT} />
      {xt.map((v) => { const x = X(v); return (
        <g key={`x${v}`}><line x1={x} x2={x} y1={r.y + r.h} y2={r.y + r.h + tl} stroke="#000" strokeWidth={0.8 * PT} />
          {labelX && <text x={x} y={r.y + r.h + tl + 3.5 * PT + fs * 0.8} fontSize={fs} textAnchor="middle">{fmtTick(v, xs)}</text>}</g>); })}
      {yt.map((v) => { const y = Y(v); return (
        <g key={`y${v}`}><line x1={r.x - tl} x2={r.x} y1={y} y2={y} stroke="#000" strokeWidth={0.8 * PT} />
          {labelY && <text x={r.x - tl - 3.5 * PT} y={y + fs * 0.35} fontSize={fs} textAnchor="end">{fmtTick(v, ys)}</text>}</g>); })}
      {xlabel && <text x={r.x + r.w / 2} y={r.y + r.h + tl + 3.5 * PT + fs * 0.8 + 4 * PT + fs} fontSize={fs} textAnchor="middle">{xlabel}</text>}
      {ylabel && <text transform={`translate(${r.x - tl - 3.5 * PT - amax(yt.map((v) => fmtTick(v, ys).length)) * fs * 0.62 - 4 * PT - fs * 0.25}, ${r.y + r.h / 2}) rotate(-90)`} fontSize={fs} textAnchor="middle">{ylabel}</text>}
    </g>);
}

export default function EngineClickCard({ piece, i, added = null, labelNow = null, labelCode = null }) {
  // added = { src: { y, p }, values }: a pulse the reviewer added, drawn the same way from the full signal
  const c = piece.clicks; const card = piece.card;
  const d = useMemo(() => cardData(card, i, added ? added.src : null), [card, i, added]);
  const imgRef = useRef(null);
  const v = (k) => (added ? (added.values[k] ?? null) : (c[k] ? c[k][i] : null));
  const { cm, tMs, freqs, W, hw, fs } = d;
  const tc = cm.cols.map((k) => tMs[k]);
  // axis limits (as figures.click_card)
  let topKhz = Math.min(fs / 2, Math.max(3 * (v('centre') || 0), 1.5 * cm.upperMax)) / 1e3;
  let ylim = [0, topKhz]; let xlim = [tMs[0], tMs[tMs.length - 1]];
  let spanT = 0; let f0z = 0; let f1z = topKhz; let spanF = 0; let loF = 0;
  if (tc.length >= 2) {
    loF = amin(cm.lower); const hiF = amax(cm.upper);
    spanF = Math.max(hiF - loF, 0.15 * hiF);
    f0z = Math.max(0, loF - 0.8 * spanF) / 1e3; f1z = Math.min(fs / 2, hiF + 0.8 * spanF) / 1e3;
    ylim = [f0z, f1z];
    spanT = Math.max(tc[tc.length - 1] - tc[0], 0.1);
    xlim = [Math.max(tMs[0], tc[0] - 1.5 * spanT), Math.min(tMs[tMs.length - 1], tc[tc.length - 1] + 1.5 * spanT)];
  }
  topKhz = ylim[1];
  // WVD image: gray_r, vmin = -0.2 vmax, vmax = max |W|
  const vmax = useMemo(() => { let m = 0; W.forEach((row) => row.forEach((x) => { if (Math.abs(x) > m) m = Math.abs(x); })); return m || 1; }, [W]);
  const nf = W.length; const nt = W[0].length;
  const dataUrl = useMemo(() => {
    const cv = document.createElement('canvas'); cv.width = nt; cv.height = nf;
    const ctx = cv.getContext('2d'); const im = ctx.createImageData(nt, nf);
    for (let k = 0; k < nf; k += 1) for (let t = 0; t < nt; t += 1) {
      const val = Math.min(1, Math.max(0, (W[k][t] + 0.2 * vmax) / (1.2 * vmax))); const g = Math.round(255 * (1 - val));
      const p = ((nf - 1 - k) * nt + t) * 4; im.data[p] = g; im.data[p + 1] = g; im.data[p + 2] = g; im.data[p + 3] = 255;
    }
    ctx.putImageData(im, 0, 0); return cv.toDataURL();
  }, [W, vmax, nf, nt]);
  useEffect(() => { if (imgRef.current) imgRef.current.setAttribute('href', dataUrl); }, [dataUrl]);
  const dtMs = tMs[1] - tMs[0]; const dfK = (freqs[1] - freqs[0]) / 1e3;
  const pmax = amax(d.pxx) || 1;
  const spec = d.pxx.map((p, k) => [(p / pmax) ** 2, d.f[k] / 1e3]);
  // autoscale with 5 % margins over the samples shown (waveform + envelope), as matplotlib
  const inView = Array.from(d.seg).map((a, k) => [tMs[k], a, d.env[k]]).filter(([t]) => t >= tMs[0] && t <= tMs[tMs.length - 1]);
  const lo = amin(inView.map((q) => q[1])); const hi = amax(inView.map((q) => Math.max(q[1], q[2])));
  const tyl = [lo - 0.05 * (hi - lo), hi + 0.05 * (hi - lo)];
  // what the pulse is NOW (after the reviewer's edits), not the engine's first label; the engine's label is added
  // when it differs (9 Oct 2026 — the card said "shrimp" for clicks the reviewer had put into a train)
  const engLab = added ? null : v('label');
  const nm = (l) => LABEL[l]?.[0] || l;
  const labelLine = labelNow
    ? `${labelNow}${engLab && labelCode && engLab !== labelCode ? ` (engine: ${nm(engLab)})` : ''}`
    : (engLab ? nm(engLab) : (added ? 'added by you' : ''));
  const title = `${card.title} · click ${v('no')} at ${(v('t') ?? 0).toFixed(4)} s`;
  const lines = [
    `Length ${nanFmt(v('wvdLen'), (x) => (x * 1e6).toFixed(0))} µs   Sweep ${nanFmt(v('wvdSweep'), (x) => `${x >= 0 ? '+' : '-'}${Math.abs(x / 1e3).toFixed(1)}`)} kHz   Width ${nanFmt(v('wvdWidth'), (x) => (x / 1e3).toFixed(1))} kHz`,
    `Peak ${nanFmt(v('peak'), (x) => (x / 1e3).toFixed(1))} kHz   Centre ${nanFmt(v('centre'), (x) => (x / 1e3).toFixed(1))} kHz   -3 dB BW ${nanFmt(v('bw3'), (x) => (x / 1e3).toFixed(1))}   RMS BW ${nanFmt(v('rmsbw'), (x) => (x / 1e3).toFixed(1))} kHz`,
    `Dur -10 dB ${nanFmt(v('dur10'), (x) => x.toFixed(0))} µs   SNR ${nanFmt(v('snr'), (x) => x.toFixed(0))} dB   ${labelLine}${v('axis') ? ` · ${v('axis')}` : ''}${v('clipped') ? ' · CLIPPED' : ''}`,
  ];
  const tmax = cm.tMax * 1e3 - (hw / fs) * 1e3;
  const kMax = tc.length ? tc.reduce((bi, t, j) => (Math.abs(t - tmax) < Math.abs(tc[bi] - tmax) ? j : bi), 0) : 0;
  const poly = (pts, X, Y) => pts.map(([a, b]) => `${X(a)},${Y(b)}`).join(' ');
  const fs9 = 9 * PT; const fs7 = 7 * PT;

  return (
    <svg viewBox={`0 0 ${FW} ${FH}`} className="ecc" role="img" aria-label={`Click card: ${title}`} fontFamily="DejaVu Sans, Verdana, Arial, sans-serif">
      <rect width={FW} height={FH} fill="#fff" />
      <defs><marker id="ecc-ar" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="4.5" markerHeight="4.5" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="#111" /></marker>
        <marker id="ecc-ab" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="4.5" markerHeight="4.5" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="#1a1aff" /></marker></defs>
      {/* rotated spectrum */}
      <Axis id="ecc-s" r={G.s} xlim={[1.05, -0.05]} ylim={ylim} xlabel="Rel. power" ylabel="Frequency (kHz)">
        {(X, Y) => (<>
          <polyline points={poly(spec, X, Y)} fill="none" stroke="#262626" strokeWidth={1 * PT} />
          <line x1={G.s.x} x2={G.s.x + G.s.w} y1={Y(v('peak') / 1e3)} y2={Y(v('peak') / 1e3)} stroke="#d62728" strokeDasharray="5 3" strokeWidth={0.8 * PT} />
          <line x1={G.s.x} x2={G.s.x + G.s.w} y1={Y(v('centre') / 1e3)} y2={Y(v('centre') / 1e3)} stroke="#1f77b4" strokeDasharray="1.5 2.5" strokeWidth={1 * PT} />
        </>)}
      </Axis>
      {/* Wigner-Ville with contour and annotations */}
      <Axis id="ecc-w" r={G.w} xlim={xlim} ylim={ylim} labelX={false} labelY={false}>
        {(X, Y) => {
          const re = tc.length ? cm.ridge.map((f) => f / 1e3) : [];
          const xs = tc.length ? tc[0] - 0.35 * spanT : 0;
          const yl = (loF - 0.35 * spanF) / 1e3;
          return (<>
            <image ref={imgRef} href={dataUrl} x={X(tMs[0] - dtMs / 2)} y={Y(freqs[nf - 1] / 1e3 + dfK / 2)} width={X(tMs[nt - 1] + dtMs / 2) - X(tMs[0] - dtMs / 2)}
                   height={Y(freqs[0] / 1e3 - dfK / 2) - Y(freqs[nf - 1] / 1e3 + dfK / 2)} preserveAspectRatio="none" style={{ imageRendering: 'pixelated' }} />
            <polyline points={poly(tc.map((t, j) => [t, cm.upper[j] / 1e3]), X, Y)} fill="none" stroke="#ff7f0e" strokeWidth={1.3 * PT} />
            <polyline points={poly(tc.map((t, j) => [t, cm.lower[j] / 1e3]), X, Y)} fill="none" stroke="#17becf" strokeWidth={1.3 * PT} />
            <polyline points={poly(tc.map((t, j) => [t, re[j]]), X, Y)} fill="none" stroke="#d62728" strokeDasharray="5 3" strokeWidth={1 * PT} />
            <circle cx={X(tmax)} cy={Y(cm.fMax / 1e3)} r={4 * PT} fill="none" stroke="#d62728" strokeWidth={1 * PT} />
            {tc.length >= 2 && (<>
              <line x1={X(xs)} x2={X(tc[0])} y1={Y(re[0])} y2={Y(re[0])} stroke="#111" strokeWidth={0.6 * PT} />
              <line x1={X(xs)} x2={X(tc[tc.length - 1])} y1={Y(re[re.length - 1])} y2={Y(re[re.length - 1])} stroke="#111" strokeWidth={0.6 * PT} />
              <line x1={X(xs)} x2={X(xs)} y1={Y(re[0])} y2={Y(re[re.length - 1])} stroke="#111" strokeWidth={1.1 * PT} markerStart="url(#ecc-ar)" markerEnd="url(#ecc-ar)" />
              <text x={X(xs - 0.03 * spanT)} y={Y((re[0] + re[re.length - 1]) / 2) + fs9 * 0.35} fontSize={fs9} fontWeight="bold" textAnchor="end">Sweep</text>
              <line x1={X(tc[0])} x2={X(tc[tc.length - 1])} y1={Y(yl)} y2={Y(yl)} stroke="#111" strokeWidth={1.1 * PT} markerStart="url(#ecc-ar)" markerEnd="url(#ecc-ar)" />
              <line x1={X(tc[0])} x2={X(tc[0])} y1={Y(yl)} y2={Y(cm.lower[0] / 1e3)} stroke="#111" strokeWidth={0.6 * PT} />
              <line x1={X(tc[tc.length - 1])} x2={X(tc[tc.length - 1])} y1={Y(yl)} y2={Y(cm.lower[cm.lower.length - 1] / 1e3)} stroke="#111" strokeWidth={0.6 * PT} />
              <text x={X((tc[0] + tc[tc.length - 1]) / 2)} y={Y(yl - 0.04 * (f1z - f0z)) + fs9 * 0.8} fontSize={fs9} fontWeight="bold" textAnchor="middle">Length</text>
              <line x1={X(tc[kMax])} x2={X(tc[kMax])} y1={Y(cm.lower[kMax] / 1e3)} y2={Y(cm.upper[kMax] / 1e3)} stroke="#1a1aff" strokeWidth={1.1 * PT} markerStart="url(#ecc-ab)" markerEnd="url(#ecc-ab)" />
              <text x={X(tc[kMax] + 0.05 * spanT)} y={Y(cm.lower[kMax] / 1e3 - 0.02 * (f1z - f0z)) + fs9 * 0.8} fontSize={fs9} fontWeight="bold" fill="#1a1aff">Width</text>
              <path d={`M${X(tc[0])},${Y(re[0]) - 3 * PT} l${3 * PT},${5.2 * PT} l${-6 * PT},0 z`} fill="#111" />
            </>)}
          </>);
        }}
      </Axis>
      <text x={G.w.x + G.w.w / 2} y={G.w.y - 6 * PT} fontSize={8.5 * PT} textAnchor="middle">{title}</text>
      {/* measurement box (upper right) */}
      <g>
        {(() => { const lh = fs7 * 1.2; const wBox = amax(lines.map((l) => l.length)) * fs7 * 0.56 + 10; const hBox = lh * 3 + 8;
          const x1 = G.w.x + 0.99 * G.w.w; const y0 = G.w.y + 0.02 * G.w.h;
          return (<>
            <rect x={x1 - wBox} y={y0} width={wBox} height={hBox} fill="#fff" fillOpacity="0.92" stroke="#b3b3b3" />
            {lines.map((l, j) => <text key={j} x={x1 - 5} y={y0 + 4 + lh * (j + 0.85)} fontSize={fs7} textAnchor="end" style={{ whiteSpace: 'pre' }} xmlSpace="preserve">{l}</text>)}
          </>); })()}
      </g>
      {/* legend (lower right) */}
      <g>
        {(() => { const items = [['line', '#ff7f0e', 'upper edge'], ['line', '#17becf', 'lower edge'], ['dash', '#d62728', 'ridge (sweep)'],
          ['circle', '#d62728', 'max amplitude'], ['tri', '#111', 'start point']];
        const lh = fs7 * 1.45; const wL = 150; const hL = lh * items.length + 8; const x1 = G.w.x + G.w.w - 6; const y1 = G.w.y + G.w.h - 6;
        return (<>
          <rect x={x1 - wL} y={y1 - hL} width={wL} height={hL} fill="#fff" fillOpacity="0.85" stroke="#ccc" rx="3" />
          {items.map(([k, col, name], j) => { const y = y1 - hL + 4 + lh * (j + 0.6); const xa = x1 - wL + 8; return (
            <g key={name}>
              {k === 'line' && <line x1={xa} x2={xa + 26} y1={y} y2={y} stroke={col} strokeWidth={1.3 * PT} />}
              {k === 'dash' && <line x1={xa} x2={xa + 26} y1={y} y2={y} stroke={col} strokeWidth={1 * PT} strokeDasharray="5 3" />}
              {k === 'circle' && <circle cx={xa + 13} cy={y} r={4 * PT} fill="none" stroke={col} strokeWidth={1 * PT} />}
              {k === 'tri' && <path d={`M${xa + 13},${y - 5} l5,8.5 l-10,0 z`} fill={col} />}
              <text x={xa + 34} y={y + fs7 * 0.35} fontSize={fs7}>{name}</text>
            </g>); })}
        </>); })()}
      </g>
      {/* waveform + envelope */}
      <Axis id="ecc-t" r={G.t} xlim={xlim} ylim={tyl} xlabel="Time from click peak (ms)" ylabel="Amplitude">
        {(X, Y) => (<>
          <polyline points={poly(Array.from(d.seg, (a, k) => [tMs[k], a]), X, Y)} fill="none" stroke="#1a1a1a" strokeWidth={0.8 * PT} />
          <polyline points={poly(d.env.map((a, k) => [tMs[k], a]), X, Y)} fill="none" stroke="#d62728" strokeOpacity="0.6" strokeWidth={0.6 * PT} />
        </>)}
      </Axis>
    </svg>
  );
}
